import {observer} from 'mobx-react-lite';
import React, {useEffect, useState} from 'react';
import {SubscriptionDialogProps} from '../../../../entity/props/subscriptions/subscription-dialog-props';
import {SubscriptionPlan} from '../../../../entity/enums/subscription-plan';
import {SubscriptionsResponseObject} from '../../../../entity/resource/subscriptions-response';
import {useService} from '../../../../infrastructure/service/service';
import {
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog, DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {User} from '../../../../entity/model/user';
import LoadingComponent from '../widget/LoadingComponent';
import Box from '@mui/material/Box';
import Skeleton from 'react-loading-skeleton';
import ConfirmDialog from '../widget/ConfirmDialog';
import {KeyValueResource} from '../../../../entity/resource/key-value-resource';

import '../../../../assets/css/style.css';
import '../../../../assets/css/subscription/subscription-dialog.css';

const SubscriptionDialog = (props: SubscriptionDialogProps): JSX.Element => {
  const {subscriptionService, userService, organizationService} = useService();

  const [loading, setLoading] = useState<boolean>(false);
  const [currentUser, setCurrentUser] = useState<User>();
  const [cancelSubscriptionLoading, setCancelSubscriptionLoading] = useState<boolean>(false);
  const [updatePaymentLoading, setUpdatePaymentLoading] = useState<boolean>(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState<boolean>(false);
  const [selectedSubscription, setSelectedSubscription] = useState<SubscriptionsResponseObject>();
  const [subscriptionDetails, setSubscriptionDetails] = useState<KeyValueResource[]>([]);

  useEffect(() => {
    const init = async (): Promise<void> => {
      setLoading(true);

      const subscription = props.subscriptions.find((subscriptionsResponseObject: SubscriptionsResponseObject): boolean => subscriptionsResponseObject.status === props.status);
      setSelectedSubscription(subscription);

      const user = await userService.getCurrentUser();
      const organization = await organizationService.getOrganizationById(user?.organizationID ?? '');
      setCurrentUser(user);

      setSubscriptionDetails([
        { label: 'Active Plan', value: subscription?.cancel_at_period_end ? 'Cancelled at the End of the Period' : props.plan === SubscriptionPlan.SOLO ? 'Solo' : 'Trial'},
        { label: 'Started', value: subscription && subscription.current_period_start !== null ? new Date(subscription.current_period_start * 1000).toDateString() : ''},
        { label: 'Expired', value: subscription && subscription.current_period_end !== null ? new Date(subscription.current_period_end * 1000).toDateString() : ''},
        { label: 'Organization', value: organization?.name ?? ''},
        { label: 'Price', value: props.plan === SubscriptionPlan.SOLO ? `$${subscription ? subscription.plan.amount / 100 : 100}.00` : 'Free'}
      ]);

      setLoading(false);
    }

    init();
  }, []);

  const cancelSubscription = async (): Promise<void> => {
    setCancelSubscriptionLoading(true);

    await subscriptionService.cancelSubscription(currentUser?.organizationID ?? '', selectedSubscription?.id ?? '');

    setCancelSubscriptionLoading(false);
    setOpenConfirmDialog(false);

    props.setOpen(false);
    props.setReload(true);
  }

  const handleCancelSubscription = (): void => {
    setOpenConfirmDialog(true);
  }

  const handleUpdatePaymentMethod = async (): Promise<void> => {
    setUpdatePaymentLoading(true);

    const response = await subscriptionService.updatePaymentMethod(currentUser?.organizationID ?? '');

    setUpdatePaymentLoading(false);
    if (response)
      window.location.href = response.url;
  }

  return (
    <>
      {
        loading
          ? <LoadingComponent/>
          : <Dialog open={props.open}>
              <DialogTitle className={'zitles-dialog-title'}>
                <span>My Subscription</span>
                <IconButton id={'btn-close-subscription-dialog'} onClick={(): void => props.setOpen(false)}>
                  <CloseIcon className={'dialog-icon'}/>
                </IconButton>
              </DialogTitle>
              <DialogContent dividers>
                <Card className={'view-subscription-card'} elevation={0}>
                  <CardContent>
                    {
                      subscriptionDetails.map((data: KeyValueResource, index: number): JSX.Element =>
                        <Box key={index} className={'template-item'}>
                          <Typography id={'text-subscription-label'} className={'template-data-label user'}>{data.label}</Typography>
                          <Typography id={'text-subscription-value'} className={'template-data-value'}>{data.value ?? <Skeleton/>}</Typography>
                        </Box>
                      )
                    }
                  </CardContent>
                </Card>
              </DialogContent>
              <DialogActions className={'action-btn-container'}>
                <Button
                  id={'btn-cancel-subscription'}
                  disableTouchRipple
                  onClick={handleCancelSubscription}
                  className={'zitles-button cancel'}>
                  Cancel Subscription
                </Button>
                <Button
                  id={'btn-update-payment-method'}
                  disabled={updatePaymentLoading}
                  className={`zitles-button confirm submit ${updatePaymentLoading ? 'disable' : ''}`}
                  onClick={handleUpdatePaymentMethod}
                  startIcon={updatePaymentLoading ? <CircularProgress size={14} color="inherit" /> : null}>
                  {
                    updatePaymentLoading ? 'Updating' : 'Update Payment Method'
                  }
                </Button>
              </DialogActions>
            </Dialog>
      }
      {
        openConfirmDialog &&
          <ConfirmDialog
            open={openConfirmDialog}
            setOpen={setOpenConfirmDialog}
            title={'Cancel Subscription'}
            message={`Are you sure you want to cancel this ${props.plan === SubscriptionPlan.TRIAL ? 'trial' : 'subscription'} ?`}
            handleSubmit={cancelSubscription}
            loading={cancelSubscriptionLoading}/>
      }
    </>
  );
}

export default observer(SubscriptionDialog);
